import React from 'react';
import { 
  Building2, Phone, Mail, MapPin, ShieldCheck, 
  ExternalLink, GraduationCap, Award, Lock, ArrowUp 
} from 'lucide-react';
import { INSTITUTION_INFO } from '../data/portalData';
import { SocialStandardisedLinks } from './SocialLinks';

export const Footer = ({ setActiveView, onSelectProgramme }) => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDegreeClick = (progId) => {
    if (onSelectProgramme) {
      onSelectProgramme(progId);
    } else {
      setActiveView('programme-detail');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#030F1C] text-slate-300 border-t-4 border-[#7A470E] print:hidden">
      
      {/* Top Banner with Intended Domain Confirmation in Executive Navy */}
      <div className="bg-[#06182B] py-3 px-4 border-b border-slate-800 text-xs text-slate-300">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center space-x-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#B87A28] animate-pulse"></span>
            <span className="text-white font-medium">Intended Official Sub-domain:</span>
            <strong className="text-[#B87A28] font-mono font-bold">https://{INSTITUTION_INFO.domain}</strong>
          </div>
          <div className="flex items-center space-x-4 text-[11px]">
            <span className="text-slate-300">Akwa Ibom State University, Nigeria</span>
            <span className="text-[#7A470E]">•</span>
            <span className="text-white font-bold font-sc">Postgraduate Executive School</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          
          {/* Col 1 & 2: Brand Identity */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center space-x-3.5">
              <img 
                src="/Logo.png" 
                alt="AKSU Official Crest" 
                className="w-16 h-16 object-contain logo-glow select-none"
              />
              <div>
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block font-sc">
                  Akwa Ibom State University
                </span>
                <span className="text-2xl font-black font-sc text-[#B87A28] tracking-wide block">
                  BUSINESS SCHOOL
                </span>
                <span className="text-[11px] text-white font-black uppercase tracking-widest block font-sc">
                  {INSTITUTION_INFO.motto}
                </span>
              </div>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed max-w-sm">
              An executive postgraduate institute committed to moulding entrepreneurial champions, 
              strategic public sector administrators, and ethical financial leaders for Africa's economic transformation.
            </p>

            <div className="pt-2 text-xs space-y-2 text-slate-300">
              <div className="flex items-center">
                <ShieldCheck className="w-4 h-4 text-[#B87A28] mr-2 shrink-0" />
                <span>NUC Full Accreditation for all Postgraduate Licences</span>
              </div>
              <div className="flex items-center">
                <Award className="w-4 h-4 text-[#7A470E] mr-2 shrink-0" />
                <span>Affiliated with ICAN, CITN & NIM Executive Boards</span>
              </div>
            </div>

            {/* Official Social Media Channels */}
            <div className="pt-3 border-t border-slate-800/80">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block font-sc mb-2">
                Official Channels
              </span>
              <SocialStandardisedLinks />
            </div>
          </div>

          {/* Col 3: Academic Programmes (Dedicated Pages) */}
          <div className="space-y-3 text-xs">
            <h4 className="text-[#B87A28] font-black font-sc text-sm uppercase tracking-wider border-b border-slate-800 pb-2">
              Academic Degrees
            </h4>
            <ul className="space-y-2 text-slate-300">
              <li>
                <button 
                  onClick={() => handleDegreeClick('phd-ba')} 
                  className="hover:text-[#B87A28] transition-colors text-left cursor-pointer flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform">PhD in Business Administration</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleDegreeClick('dba')} 
                  className="hover:text-[#B87A28] transition-colors text-left cursor-pointer flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform">Doctor of Business Administration (DBA)</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleDegreeClick('mba')} 
                  className="hover:text-[#B87A28] transition-colors text-left cursor-pointer flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform">Master of Business Administration (MBA)</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleDegreeClick('pgd-ba')} 
                  className="hover:text-[#B87A28] transition-colors text-left cursor-pointer flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform">Postgraduate Diploma (PGD)</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleDegreeClick('dpa')} 
                  className="hover:text-[#B87A28] transition-colors text-left cursor-pointer flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform">Doctor of Public Administration (DPA)</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleDegreeClick('phd-af')} 
                  className="hover:text-[#B87A28] transition-colors text-left cursor-pointer flex items-center group"
                >
                  <span className="group-hover:translate-x-1 transition-transform">Doctor of Accounting and Finance</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Col 4: Portals & Lifecycle */}
          <div className="space-y-3 text-xs">
            <h4 className="text-[#B87A28] font-black font-sc text-sm uppercase tracking-wider border-b border-slate-800 pb-2">
              Executive Portals
            </h4>
            <ul className="space-y-2 text-slate-300">
              <li>
                <button 
                  onClick={() => setActiveView('class-timetable')}
                  className="hover:text-amber-300 text-emerald-400 font-bold transition-colors text-left flex items-center space-x-1 cursor-pointer font-sc"
                >
                  <span>Class Timetable (2025/2026)</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setActiveView('admission-advert')}
                  className="hover:text-[#B87A28] text-[#B87A28] font-bold transition-colors text-left flex items-center space-x-1 cursor-pointer font-sc"
                >
                  <span>Admission Advert (Official Circular)</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setActiveView('apply')}
                  className="hover:text-[#B87A28] transition-colors text-left flex items-center space-x-1 cursor-pointer"
                >
                  <span>Apply for Admission (₦30,000)</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setActiveView('screening')}
                  className="hover:text-[#B87A28] transition-colors text-left flex items-center space-x-1 cursor-pointer"
                >
                  <span>Screening & Verification Portal</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setActiveView('offer-letter')}
                  className="hover:text-[#B87A28] transition-colors text-left flex items-center space-x-1 cursor-pointer"
                >
                  <span>Admission Letter & QR Check</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setActiveView('enrolment')}
                  className="hover:text-[#B87A28] transition-colors text-left flex items-center space-x-1 cursor-pointer"
                >
                  <span>Enrolment & Student ID Portal</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    setActiveView('lms-hub');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="hover:text-amber-300 font-bold transition-colors text-left flex items-center space-x-1 cursor-pointer text-amber-400"
                >
                  <Lock className="w-3.5 h-3.5 mr-1 text-amber-300" />
                  <span>Portal Login (Central Hub)</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Col 5: Campuses & Inquiries */}
          <div className="space-y-3 text-xs">
            <h4 className="text-[#B87A28] font-black font-sc text-sm uppercase tracking-wider border-b border-slate-800 pb-2">
              Campuses & Liaison Office
            </h4>
            <div className="space-y-3 text-slate-300">
              <div>
                <strong className="text-white block font-sc">Obio Akpa Campus (Operating Campus):</strong>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Obio Akpa, Oruk Anam LGA, P.M.B 1167, Uyo, Akwa Ibom State.
                </p>
              </div>

              <div>
                <strong className="text-white block font-sc">AKSU Liaison Office (Uyo):</strong>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  6 Tony Asikpo Street, Shelter Afrique, P.M.B 1167, Uyo, Akwa Ibom State.
                </p>
              </div>

              <div>
                <strong className="text-white block font-sc">Main Campus (Headquarters):</strong>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Ikot Akpaden, Mkpat Enin LGA, P.M.B 1167, Uyo, Akwa Ibom State.
                </p>
              </div>

              <div className="pt-2 border-t border-slate-800 space-y-1.5 font-mono text-[11px]">
                <div className="text-slate-300">
                  <span className="text-[#B87A28] font-bold">Helplines:</span> 08033380401, 07012550607
                </div>
                <div className="text-slate-300">
                  <span className="text-[#B87A28] font-bold">Emails:</span> aisubs@aksu.edu.ng, uwemuwah@aksu.edu.ng
                </div>
                <div className="text-slate-300">
                  <span className="text-[#B87A28] font-bold">Portal:</span> https://businessschool.aksu.edu.ng/admissions
                </div>
                <div className="text-slate-300">
                  <span className="text-[#B87A28] font-bold">Study:</span> www.aksu.edu.ng/study
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Sub-footer with Legal Directives */}
        <div className="mt-14 pt-8 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-4 text-xs text-slate-400">
          <div className="flex flex-wrap items-center gap-3">
            <span>© {new Date().getFullYear()} Akwa Ibom State University Business School. All Rights Reserved.</span>
            <span className="hidden sm:inline text-slate-600">•</span>
            <span className="text-slate-500 font-sc text-[11px]">NDPA 2023 Compliant</span>
          </div>

          {/* Legal Compliance Navigation & Tools */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px]">
            <button
              onClick={() => {
                setActiveView('privacy');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="text-slate-300 hover:text-[#B87A28] transition-colors cursor-pointer font-sc"
            >
              Privacy Policy
            </button>
            <span className="text-slate-700">•</span>
            <button
              onClick={() => {
                setActiveView('terms');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="text-slate-300 hover:text-[#B87A28] transition-colors cursor-pointer font-sc"
            >
              Site Terms
            </button>
            <span className="text-slate-700">•</span>
            <button
              onClick={() => {
                setActiveView('cookies');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="text-slate-300 hover:text-[#B87A28] transition-colors cursor-pointer font-sc"
            >
              Cookie Note
            </button>
            <span className="text-slate-700 hidden md:inline">•</span>
            <span className="text-slate-400 hidden md:inline">NUC Benchmark Compliant</span>
            <span className="text-slate-700 hidden md:inline">•</span>
            <span className="text-slate-400 hidden md:inline">Paystack Verified</span>
            <span className="text-slate-700">•</span>
            <button 
              onClick={scrollToTop}
              className="text-[#B87A28] hover:underline flex items-center space-x-1 cursor-pointer font-sc"
            >
              <span>Back to Top</span>
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

      </div>
    </footer>
  );
};
